import React from 'react';
import PropTypes from 'prop-types';
import styled, {css} from 'styled-components';
import {
  copyFontFamily,
  copyFontSize,
  copyFontSizeSmall,
  copyColor,
  colorWhite,
  colorNearWhite,
  colorLighter,
  focusOutlineColor,
  focusOutlineStyle,
  focusOutlineWidth,
  focusOutlineOffset,
  formSelectedToggleColor
} from '@nib-components/theme';
import {ChevronRightSystemIcon, IconProps} from '@nib/icons';
import {Box, BoxProps, Columns, Column} from '@nib/layout';

export interface UtilityButtonProps extends BoxProps {
  icon?: React.FC<IconProps>;
  showBorder?: boolean;
  showActionIcon?: boolean;
  actionIcon?: React.FC<IconProps>;
  selected?: boolean;
  href?: string;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent) => void;
  component?: React.ReactNode;
  children: React.ReactNode;
  [key: string]: unknown; // other props
}

export interface UtilityButtonTitleProps {
  truncate?: boolean;
  children: React.ReactNode;
  [key: string]: unknown; // other props
}

const truncatedTextStyles = css`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const SubTitle = styled(({truncate, ...rest}) => <div {...rest} />)<UtilityButtonTitleProps>`
  font-family: ${copyFontFamily || 'sans-serif'};
  font-size: ${copyFontSizeSmall};
  ${({truncate}) => truncate && truncatedTextStyles};
`;
SubTitle.displayName = 'UtilityButton.SubTitle';
SubTitle.propTypes = {
  truncate: PropTypes.bool,
  children: PropTypes.node
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const Title = styled(({truncate, ...rest}) => <div {...rest} />)<UtilityButtonTitleProps>`
  font-family: ${copyFontFamily || 'sans-serif'};
  font-size: ${copyFontSize};
  font-weight: 700;
  ${({truncate}) => truncate && truncatedTextStyles};
`;
Title.displayName = 'UtilityButton.Title';
Title.propTypes = {
  truncate: PropTypes.bool,
  children: PropTypes.node
};

const Wrapper = styled(Box)`
  display: block;
  cursor: pointer;
  background: ${colorWhite};
  color: ${copyColor};
  text-decoration: none;
  border: none;
  line-height: inherit;
  text-align: left;
  width: 100%;

  transition: background-color 0.25s ease-in-out;

  ${({showBorder}) =>
    showBorder &&
    css`
      box-shadow: inset 0 0px 0 1px ${colorLighter};
    `}

  ${({selected}) =>
    selected &&
    css`
      ${UtilityButton.Title} {
        color: ${formSelectedToggleColor};
      }
      box-shadow: inset 0 0px 0 2px ${formSelectedToggleColor};
    `}

  &:hover, 
  &:focus {
    background-color: ${colorNearWhite};
  }

  &:focus {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${focusOutlineColor};
    outline-offset: ${focusOutlineOffset};
  }

  svg {
    display: block;
  }

  ${({disabled}) =>
    disabled &&
    css`
      opacity: 0.4;

      &:hover,
      &:focus {
        background: ${colorWhite};
        cursor: not-allowed;
      }
    `}
`;

const FullHeightColumns = styled(Columns)`
  height: 100%;
`;

const UtilityButton: React.FC<UtilityButtonProps> & {Title: typeof Title} & {SubTitle: typeof SubTitle} = props => {
  const {icon: Icon, actionIcon: ActionIcon, showBorder, selected, showActionIcon, href, disabled, onClick, component, children, ...otherProps} = props;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const element: any = component ? component : onClick ? 'button' : 'a';

  return (
    <Wrapper
      paddingVertical={3}
      paddingHorizontal={4}
      borderRadius="standard"
      selected={selected}
      showBorder={showBorder}
      as={element}
      href={href && !disabled ? href : null}
      disabled={disabled}
      onClick={onClick}
      {...otherProps}
    >
      <FullHeightColumns space={4} verticalAlign="center">
        {Icon && (
          <Column width="content">
            <Icon size="md" fill="brandBase" />
          </Column>
        )}
        <Column>{children}</Column>

        {showActionIcon && ActionIcon && (
          <Column width="content">
            <ActionIcon size="sm" fill="dark" />
          </Column>
        )}
      </FullHeightColumns>
    </Wrapper>
  );
};

UtilityButton.displayName = 'UtilityButton';
UtilityButton.Title = Title;
UtilityButton.SubTitle = SubTitle;

UtilityButton.propTypes = {
  children: PropTypes.node.isRequired,
  icon: PropTypes.func,
  actionIcon: PropTypes.func,
  showActionIcon: PropTypes.bool,
  showBorder: PropTypes.bool,
  href: PropTypes.string,
  onClick: PropTypes.func,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  // 'any' is required due to our use of GatsbyJS's Link component which doesn't fall under any defined PropType.
  component: PropTypes.any
};

UtilityButton.defaultProps = {
  showBorder: false,
  showActionIcon: true,
  selected: false,
  disabled: false,
  actionIcon: ChevronRightSystemIcon
};

export default UtilityButton;
